import { useMemo, useState } from "react";
import { Button, Card, Dropdown, Modal } from "antd";
import type { MenuProps } from "antd";
import { TableOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faCopy,
  faEllipsis,
  faGripVertical,
  faPaste,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import type { DraggableProvidedDragHandleProps } from "react-beautiful-dnd";
import { Split } from "../../types/Programmazione";
import { useClipboard } from "../../contexts/clipboard";
import { EntityOptionsState, type ProgEditorOptions } from "../../hooks/useEntityOptions";
import { useProgrammazioneMutations } from "../../hooks/useProgrammazioneMutations";
import type { Option } from "../../types/generics";
import ExerciseListView from "./ExerciseListView";
import WeekCompareModal from "./WeekCompareModal";

type Props = {
  split: Split;
  splitIndex: number;
  canEdit: boolean;
  isTargetAthlete: boolean;
  entityOpts: EntityOptionsState;
  progEditorOptions?: ProgEditorOptions;
  mutations: ReturnType<typeof useProgrammazioneMutations>;
  exercisesOptions: Option[];
  muscleGroupOptions: Option[];
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
};

export default function SplitCardView({
  split,
  splitIndex,
  canEdit,
  isTargetAthlete,
  entityOpts,
  progEditorOptions,
  mutations,
  exercisesOptions,
  muscleGroupOptions,
  dragHandleProps,
}: Props) {
  const { copiedSplit, copySplit } = useClipboard();
  // Splits: first one EXPANDED so the user lands on their plan, the rest
  // collapsed to keep long programmazioni navigable. (The EXERCISE cards inside
  // start collapsed by default — see ExerciseCardView — so an expanded split
  // still reads as a compact, scannable list.)
  const [collapsed, setCollapsed] = useState(splitIndex !== 0);

  const toggle = () => setCollapsed((v) => !v);

  // T3.B (#cdd80cd0) — Confronta settimane: hidden when no exercise in this
  // split has any week data; UX spec on bde/93f6e820.
  const [compareOpen, setCompareOpen] = useState(false);
  const hasAnyWeeks = useMemo(
    () =>
      (split.exercises ?? []).some(
        (e) => (e.progression?.weeks?.length ?? 0) > 0,
      ),
    [split.exercises],
  );

  const exCount = split.exercises?.length || 0;
  // Compact one-line preview of the exercises, shown only when collapsed so a
  // split can be scanned without expanding it.
  const exSummary = useMemo(
    () =>
      (split.exercises ?? [])
        .map((e) => e.customName?.trim() || "Esercizio")
        .join(" · "),
    [split.exercises],
  );

  return (
    <Card
      className="split-card mb-3"
      id={`split-card-${splitIndex}`}
      // PROG-DETAIL/IMPL I5 — scroll-margin-top so the index-chip scroll
      // (and any future sticky-header pin on expand) lands with the card
      // header visible instead of behind the sticky page header.
      style={{ scrollMarginTop: 96 }}
      styles={{ body: { padding: collapsed ? "12px 16px" : "12px 16px 16px" } }}
    >
      <div className="split-card-header">
        {dragHandleProps && (
          <span
            {...dragHandleProps}
            onClick={(e) => e.stopPropagation()}
            className="split-card-grip"
            title="Trascina per riordinare"
          >
            <FontAwesomeIcon icon={faGripVertical} />
          </span>
        )}
        <button
          type="button"
          onClick={toggle}
          aria-label={collapsed ? "Espandi split" : "Comprimi split"}
          aria-expanded={!collapsed}
          className="split-card-toggle"
        >
          <span className="split-num-badge">{splitIndex + 1}</span>
          <span className="split-card-titles">
            <span className="split-card-title">{split.dayName}</span>
            <span className="split-card-meta">
              {exCount} eserciz{exCount !== 1 ? "i" : "io"}
              {collapsed && exSummary ? ` · ${exSummary}` : ""}
            </span>
          </span>
          <FontAwesomeIcon
            icon={collapsed ? faChevronRight : faChevronDown}
            className="split-card-chevron"
          />
        </button>
        <div onClick={(e) => e.stopPropagation()} className="split-card-actions">
          {(() => {
            const items: MenuProps["items"] = [];
            if (hasAnyWeeks)
              items.push({
                key: "compare",
                icon: <TableOutlined />,
                label: "Confronta settimane",
                onClick: () => setCompareOpen(true),
              });
            if (canEdit) {
              if (items.length > 0) items.push({ type: "divider" });
              items.push({
                key: "copy",
                icon: <FontAwesomeIcon icon={faCopy} />,
                label: "Copia Split",
                onClick: () => copySplit(split),
              });
              if (copiedSplit)
                items.push({
                  key: "paste",
                  icon: <FontAwesomeIcon icon={faPaste} />,
                  label: "Incolla Split",
                  onClick: () => mutations.pasteSplit(copiedSplit),
                });
              items.push({ type: "divider" });
              items.push({
                key: "delete",
                danger: true,
                icon: <FontAwesomeIcon icon={faTrash} />,
                label: "Elimina Split",
                onClick: () =>
                  Modal.confirm({
                    title: "Eliminare lo split?",
                    content: `"${split.dayName}" e tutti i suoi esercizi verranno rimossi.`,
                    okText: "Elimina",
                    okButtonProps: { danger: true },
                    cancelText: "Annulla",
                    onOk: () => mutations.deleteSplit(splitIndex),
                  }),
              });
            }
            if (items.length === 0) return null;
            return (
              <Dropdown trigger={["click"]} menu={{ items }} placement="bottomRight">
                <Button
                  type="text"
                  icon={<FontAwesomeIcon icon={faEllipsis} />}
                  aria-label="Azioni split"
                  title="Azioni split"
                />
              </Dropdown>
            );
          })()}
        </div>
      </div>

      <div style={{ display: collapsed ? "none" : "block" }}>
        <div className="mt-3">
          <ExerciseListView
            exercises={split.exercises || []}
            splitIndex={splitIndex}
            canEdit={canEdit}
            isTargetAthlete={isTargetAthlete}
            entityOpts={entityOpts}
            progEditorOptions={progEditorOptions}
            mutations={mutations}
            exercisesOptions={exercisesOptions}
            muscleGroupOptions={muscleGroupOptions}
          />
        </div>
      </div>

      {compareOpen && (
        <WeekCompareModal
          open={compareOpen}
          onClose={() => setCompareOpen(false)}
          split={split}
          splitIndex={splitIndex}
          mutations={mutations}
          canEdit={canEdit}
          isTargetAthlete={isTargetAthlete}
        />
      )}
    </Card>
  );
}
